import React, {useCallback, useRef, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {structuredTableActionOrigin} from 'analytics/Util';
import {assert} from 'Asserts';
import {ActionOrigin, ANALYTICS_MODULE, COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {SampleDetails} from 'constants/SampleConstants';
import {AddFromStructuredTablePasteOption} from 'notebook/sample-propagation/AddFromStructuredTableConstants';
import getSampleDescriptor from 'notebook/sample-propagation/util/getSampleDescriptor';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {convertStructuredTableColumnsToAnalyticsPayload} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingsAnalytics';
import {
  StructuredTableAddOperationOptions,
  StructuredTableColumn,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import {isTableLengthLocked} from 'notebook/structured-tables/util/isTableLengthLocked';
import {Note} from 'notebook/util/NoteUtilConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import useOnChangeOnly from 'ui-library/hooks/useOnChangeOnly';
import useOnMount from 'ui-library/hooks/useOnMount';
import usePrevious from 'ui-library/hooks/usePrevious';
import Modal from 'ui-library/modals/Modal';
import * as logging from 'util/Logging';

import SamplesSourceCollector, {SamplesSource} from '../sample-collection/SamplesSourceCollector';
import {SampleFilter, SampleFilters} from '../SampleFilter';
import {maximumNumberOfSamplesThatCanBeAddedBeforeExpansion} from '../util/addSamplesToTable';
import AddSamplesModalFormControls from './AddSamplesModalFormControls';
import nonExpansionFooterMessage from './nonExpansionFooterMessage';

export const ADD_FROM_STRUCTURED_TABLE_MODAL_TYPE = 'add_from_structured_table';

export interface DestinationSampleFiltersAndColumns {
  // Use sampleFilters and ignore sampleFilter
  sampleFilters: Array<SampleFilter>;
  sampleFilter: SampleFilter;
  // Destination columns (almost always only one column, two if it is a Well)
  columns: Array<StructuredTableColumn>;
  columnDefinitions: Array<ColumnDefinition | null>;
  columnIndices: Array<number>; // Indices of destination columns within destination table
  destinationDisplayName: string;
}

/**
 * Modal for adding samples from one structured table to another, or to a run field.
 */
interface AddSamplesFromStructuredTableModalPropsBase {
  sessionId: string; // For analytics
  entryId: string;
  limitSamplesTo?: SampleFilters;
  // The returned samples may be duplicated. The user can indicate multiples, which are
  // rendered here as sequential duplicates. The samplesSources are included as a reference
  // for the source data, which may be useful to some callers.
  onRequestSubmit: (addSamplesCommand: {
    samples: Array<SampleDetails>;
    samplesSources: Array<SamplesSource>;
    destinationColumnsCids: Array<string> | null;
    pasteOption: AddFromStructuredTablePasteOption;
  }) => void;
  onRequestClose: () => void;
  // When the destination is another structured table, you must provide a list of
  // {sample filter, destination column array, name displayed in modal} objects.
  destinationSampleFiltersByColumn?: Array<DestinationSampleFiltersAndColumns>;

  // Otherwise, you must provide a sampleFilter, indicating what you will accept
  destinationSampleType?: SampleFilter | null;
}

// Public interface
interface AddSamplesFromStructuredTableModalProps extends AddSamplesFromStructuredTableModalPropsBase {
  note: Note | null;
  destinationTable?: UnknownStructuredTable;
  options?: StructuredTableAddOperationOptions;
  columnIndex?: number;
}

// For tests & stories
export interface AddSamplesFromStructuredTableModalWithSamplesSourcesProps
  extends AddSamplesFromStructuredTableModalPropsBase {
  samplesSources: Array<SamplesSource>;
  destinationColumn?: number; // Index of the destination column in destination table.
  destinationTable?: UnknownStructuredTable;
  options?: StructuredTableAddOperationOptions;
}

export interface AddSamplesFromStructuredTableSourceAnalyticsInfo {
  table: UnknownStructuredTable | null;
  column: StructuredTableColumn | null;
  columnDefinition: ColumnDefinition | null;
}

// This component ensures that the collector isn't re-run unnecessarily. If the collector
// is embedded in the modal component below, it will re-run on every change to the form.
export default function AddSamplesFromStructuredTableModal(
  props: AddSamplesFromStructuredTableModalProps
): JSX.Element {
  return (
    <SamplesSourceCollector
      note={props.note}
      entryId={props.entryId}
      includeInputTables={true}
      preFilter={props.limitSamplesTo}
      omitTableNames={props.destinationTable != null ? [props.destinationTable.name] : []}
    >
      {(samplesSources) => (
        <AddSamplesFromStructuredTableModalWithSamplesSources
          {...props}
          samplesSources={samplesSources}
          destinationColumn={props.columnIndex}
        />
      )}
    </SamplesSourceCollector>
  );
}

export function AddSamplesFromStructuredTableModalWithSamplesSources(
  props: AddSamplesFromStructuredTableModalWithSamplesSourcesProps
): JSX.Element {
  const [destinationColumnIndex, setDestinationColumnIndex] = useState<number>(-1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onChangeDestinationColumnIndex = useCallback(setDestinationColumnIndex, []);
  const [selectedSamples, setSelectedSamples] = useState<Array<SampleDetails>>([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onChangeSelectedSamples = useCallback(setSelectedSamples, []);
  const numberOfSelectedSamples = selectedSamples.length;
  const {destinationSampleFiltersByColumn} = props;
  // This modal can be used in two modes. When the destination is a table, the `destinationSampleFiltersByColumn`
  // prop will be an array. When the destination is a run field, it will be null.
  const hasDestinations = destinationSampleFiltersByColumn != null;
  // If there are multiple destinations, the default column index is -1 (forcing the user to make a selection
  // before proceeding).
  const hasUserSelectedDestination = destinationColumnIndex >= 0;
  // We store samplesSource in the state of this component, without a setState updater function. This allows us
  // to memoize samplesSources and store a stable value for this component and its children. This prevents
  // any external irrelevant changes that may trigger samplesSources to be recreated from affecting the modal.
  const [samplesSources] = useState(props.samplesSources);
  // The components rendered below rely on having stable (non-garbage-collected) objects in `props.samplesSources`.
  // The sources are provided by `SamplesSourceCollector`, which will not re-render while this component is
  // mounted, so the samples should remain safely in memory. However, if that pattern changes, we want to
  // alert the developer.
  const previousSources = usePrevious(props.samplesSources);
  const preventTableExpansion =
    isTableLengthLocked(props.destinationTable) || (props.options?.preventTableExpansion ?? false);
  const preventTableExpansionMessage = props.options?.preventTableExpansionMessage;
  useOnChangeOnly(() => {
    logging.info('The samplesSources property must never change.', {
      extra: {
        previousSources,
        currentSources: props.samplesSources,
        destinationColumnIndex,
      },
    });
  }, [props.samplesSources]);
  // Default is to remove duplicates, unless config flag is enabled
  const [removeDuplicates, setRemoveDuplicates] = useState<boolean>(false);

  // For analytics only
  const sourceAnalyticsInfo = useRef<AddSamplesFromStructuredTableSourceAnalyticsInfo>({
    table: null,
    column: null,
    columnDefinition: null,
  });
  const largestReplicationFactor = useRef<number>(0);

  useOnMount(() => {
    analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, ANALYTICS_MODULE.ELN, {
      actionOrigin: props.destinationTable
        ? structuredTableActionOrigin(props.destinationTable)
        : ActionOrigin.UI_UNDETERMINED,
      modalType: ADD_FROM_STRUCTURED_TABLE_MODAL_TYPE,
      sessionId: props.sessionId,
      enhancedAfstEnabled: true,
      entryId: props.entryId,
      specifiedDestinationColumn: props.destinationColumn !== undefined,
    });
  });

  const descriptor = getSampleDescriptor(
    props.destinationSampleType ??
      (hasDestinations && hasUserSelectedDestination
        ? destinationSampleFiltersByColumn[destinationColumnIndex].sampleFilter
        : null)
  );

  function trackModalCloseOrSubmit(event: analytics.AnalyticsEvent): void {
    const destinationColumnInfo = destinationSampleFiltersByColumn?.[destinationColumnIndex];
    analytics.trackEventWithModule(event, ANALYTICS_MODULE.ELN, {
      actionOrigin: props.destinationTable
        ? structuredTableActionOrigin(props.destinationTable)
        : ActionOrigin.UI_UNDETERMINED,
      modalType: ADD_FROM_STRUCTURED_TABLE_MODAL_TYPE,
      sessionId: props.sessionId,
      enhancedAfstEnabled: true,
      entryId: props.entryId,

      sourceTableProvenanceUuid: sourceAnalyticsInfo.current.table?.provenanceUuid,
      sourceTableKind: sourceAnalyticsInfo.current.table?.kind,
      destinationTableProvenanceUuid: props.destinationTable?.provenanceUuid,
      destinationTableKind: props.destinationTable?.kind,

      ...convertStructuredTableColumnsToAnalyticsPayload(
        sourceAnalyticsInfo.current.column,
        sourceAnalyticsInfo.current.columnDefinition,

        destinationColumnInfo?.columns?.[destinationColumnInfo.columns.length - 1] ?? null,
        destinationColumnInfo?.columnDefinitions?.[destinationColumnInfo.columnDefinitions.length - 1] ?? null
      ),

      pasteOption,
      removeDuplicates,
      largestReplicationFactor: largestReplicationFactor.current,
      sourceSubmitted: sourceAnalyticsInfo.current.table?.rows.every((row) => row.isFinalized),
    });
  }

  function handleClose(): void {
    trackModalCloseOrSubmit(COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL);
    props.onRequestClose();
  }

  function handleSubmit(): void {
    trackModalCloseOrSubmit(COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL);
    props.onRequestSubmit({
      samples: selectedSamples,
      samplesSources,
      destinationColumnsCids: destinationSampleFiltersByColumn
        ? destinationSampleFiltersByColumn[destinationColumnIndex].columns.map((col) => col.cid)
        : null,
      pasteOption,
    });
  }

  const [pasteOption, setPasteOption] = useState<AddFromStructuredTablePasteOption>(
    AddFromStructuredTablePasteOption.APPEND
  );

  const nonExpansionModalFooter =
    preventTableExpansion && hasDestinations && hasUserSelectedDestination
      ? nonExpansionFooterMessage({
          numberOfSelectedSamples,
          preventTableExpansionMessage:
            preventTableExpansionMessage || 'This table cannot expand with new rows.',
          sampleDescriptor: descriptor,
          maximumNumberOfSamplesBeforeExpansion:
            pasteOption === AddFromStructuredTablePasteOption.APPEND
              ? maximumNumberOfSamplesThatCanBeAddedBeforeExpansion(
                  props.destinationTable!,
                  props.destinationSampleFiltersByColumn![destinationColumnIndex].columnIndices
                )
              : props.destinationTable!.rows.length,
        })
      : null;

  // The destination column index is the index of the destination column in the list of columns that we can
  // use the AFST tool on (destinationSampleFiltersByColumn).
  let initialDestinationColumnIndex = -1; // No destination column selected by default.
  // If user right-clicked a destination column, select that destination column by default if available.
  if (destinationSampleFiltersByColumn && props.destinationColumn !== undefined) {
    initialDestinationColumnIndex = destinationSampleFiltersByColumn.findIndex(
      ({columnIndices}) =>
        // Well column filters have two column indices [plate column index, well column index], so check the last index.
        columnIndices.length > 0 && columnIndices[columnIndices.length - 1] === props.destinationColumn
    );
  }

  return (
    <Modal
      title="Add from structured table"
      onRequestSubmit={handleSubmit}
      onRequestClose={handleClose}
      buttons={[
        {
          submit: true,
          text: `Add ${descriptor.plural}${
            numberOfSelectedSamples > 0 ? ` (${numberOfSelectedSamples})` : ''
          }`,
          disabled: numberOfSelectedSamples === 0 || nonExpansionModalFooter != null,
          style: ButtonStyle.SECONDARY,
          dataTestAttrs: {component: 'AddSamplesFromStructuredTableModal', element: 'addButton'},
        },
      ]}
      footer={nonExpansionModalFooter}
      dataTestAttrs={{component: 'AddSamplesFromStructuredTableModal', element: 'root'}}
    >
      <AddSamplesModalFormControls
        samplesSources={samplesSources}
        destinationSampleFiltersByColumn={destinationSampleFiltersByColumn}
        destinationSampleType={props.destinationSampleType}
        onChangeDestinationColumnIndex={(index) => {
          assert(
            destinationSampleFiltersByColumn != null,
            'Got destination-column-change event when using AddSamples modal in a run field',
            {index, destinationSampleType: props.destinationSampleType}
          );
          assert(
            // index can be -1 during the initialization phase of the form controls component
            index < destinationSampleFiltersByColumn.length,
            'Invalid destination column index in AddSamples modal',
            {
              destinationColumnIndex,
              destinationColumns: destinationSampleFiltersByColumn.map(({columns}) =>
                columns.map((col) => col.name)
              ),
              selectedSampleCount: selectedSamples.length,
            }
          );
          onChangeDestinationColumnIndex(index);
        }}
        onChangeSourceTableOrColumn={(
          updatedSourceAnalyticsInfo: AddSamplesFromStructuredTableSourceAnalyticsInfo
        ) => {
          sourceAnalyticsInfo.current = updatedSourceAnalyticsInfo;
        }}
        onChangeLargestReplicationFactor={(updatedLargestReplicationFactor: number) => {
          largestReplicationFactor.current = updatedLargestReplicationFactor;
        }}
        onChangeSelectedSamples={onChangeSelectedSamples}
        sampleDescriptor={descriptor}
        initialDestinationColumnIndex={initialDestinationColumnIndex}
        pasteOption={pasteOption}
        onChangePasteOption={setPasteOption}
        removeDuplicates={removeDuplicates}
        onChangeRemoveDuplicates={setRemoveDuplicates}
      />
    </Modal>
  );
}
